import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  localDate,
  FACTORY_TZ,
  TASK_KIND_LABEL,
  TASK_STATUS_LABEL,
  TASK_STATUSES,
  type AreaDto,
  type EquipmentDto,
  type ProductDto,
  type ProductListDto,
  type TaskDto,
  type TaskKind,
  type TaskStatus,
} from '@app/shared';
import { api } from '../api';
import { platform } from '../platform';
import { useApp } from '../state';
import { Icon, plural, Sheet, when } from '../ui';
import { areaPath } from './Panels';
import { ProductFacts } from './Products';
import { shiftText } from './OtherTasks';

type Person = { id: string; name: string; position: string | null; areaIds: string[] };

/** Задача: что, где, кто, номенклатура (характеристики, количество, места хранения), действия по статусу. */
export function TaskSheet({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => Promise<void> }) {
  const { fail, toast, open } = useApp();
  const [t, setT] = useState<TaskDto | null>(null);
  const [p, setP] = useState<ProductDto | null>(null);
  const [areas, setAreas] = useState<AreaDto[]>([]);
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState(false);

  const load = useCallback(async () => {
    try {
      const x = await api.task(id);
      setT(x);
      if (x.productId) setP(await api.product(x.productId));
    } catch (e) {
      fail(e);
      onClose();
    }
  }, [id, fail, onClose]);
  useEffect(() => {
    void load();
    api.areas().then(setAreas).catch(() => {});
  }, [load]);

  async function run(fn: () => Promise<TaskDto>, done: string) {
    setBusy(true);
    try {
      const x = await fn();
      setT(x);
      platform.haptic('success');
      toast(done);
      await onChanged();
      return x;
    } catch (e) {
      fail(e);
      return null;
    } finally {
      setBusy(false);
    }
  }

  async function start() {
    const x = await run(() => api.startTask(id), 'Отмечено: начал выполнение');
    if (x?.checkId && (x.kind === 'check' || x.kind === 'changeover')) {
      onClose();
      open({ name: 'check', id: x.checkId });
    }
  }

  if (!t) {
    return (
      <Sheet title="Задача" onClose={onClose}>
        <div className="skeleton" />
      </Sheet>
    );
  }
  const isCheck = t.kind === 'check' || t.kind === 'changeover';
  const next: Record<TaskStatus, TaskStatus | null> = { new: 'assigned', assigned: 'in_progress', in_progress: 'done', done: null };
  const prev: Record<TaskStatus, TaskStatus | null> = { new: null, assigned: 'new', in_progress: 'assigned', done: 'in_progress' };
  const sh = shiftText(t);

  return (
    <Sheet title={TASK_KIND_LABEL[t.kind]} onClose={onClose}>
      <p className="step-title">{t.title}</p>
      <div className="row wrap" style={{ gap: 6 }}>
        <span className="badge solid">{TASK_STATUS_LABEL[t.status]}</span>
        {t.checkStatus === 'issues' && <span className="badge attention">есть замечания</span>}
        {t.checkStatus === 'ok' && <span className="badge outline">всё в порядке</span>}
      </div>
      <dl className="facts">
        {sh && (
          <div>
            <dt>Смена</dt>
            <dd>{sh}</dd>
          </div>
        )}
        {t.areaId && (
          <div>
            <dt>Участок</dt>
            <dd>{areaPath(areas, t.areaId)}</dd>
          </div>
        )}
        {t.equipmentName && (
          <div>
            <dt>Машина</dt>
            <dd>
              <button type="button" className="link-btn" onClick={() => (onClose(), open({ name: 'unit', id: t.equipmentId! }))}>
                {t.equipmentName}
              </button>
            </dd>
          </div>
        )}
        <div>
          <dt>Исполнители</dt>
          <dd>{t.assignees.length ? t.assignees.map((a) => a.name).join(', ') : 'не назначены'}</dd>
        </div>
        {t.dueAt && (
          <div>
            <dt>Срок</dt>
            <dd>{when(t.dueAt)}</dd>
          </div>
        )}
        {t.startedAt && (
          <div>
            <dt>Начал</dt>
            <dd>
              {t.startedByName} · {when(t.startedAt)}
            </dd>
          </div>
        )}
        {t.doneAt && (
          <div>
            <dt>Готово</dt>
            <dd>{when(t.doneAt)}</dd>
          </div>
        )}
        {t.createdByName && (
          <div>
            <dt>Поставил</dt>
            <dd>{t.createdByName}</dd>
          </div>
        )}
      </dl>
      {t.notes && <div className="card flat text">{t.notes}</div>}

      {t.can.start && (
        <button type="button" className="btn btn-primary btn-block" disabled={busy} onClick={() => void start()}>
          <Icon name="play" size={18} />
          {t.kind === 'check' ? 'Начать проверку' : t.kind === 'changeover' ? 'Начать переналадку' : 'Начать выполнение'}
        </button>
      )}
      {isCheck && t.checkId && (
        <button type="button" className={`btn btn-block ${t.status === 'done' ? 'btn-outline' : 'btn-primary'}`} onClick={() => (onClose(), open({ name: 'check', id: t.checkId! }))}>
          <Icon name="tasks" size={18} />
          {t.status === 'done' ? 'Открыть отчёт' : 'Продолжить'}
        </button>
      )}
      {!isCheck && t.can.move && t.status === 'in_progress' && (
        <button type="button" className="btn btn-primary btn-block" disabled={busy} onClick={() => void run(() => api.patchTask(id, { status: 'done' }), 'Готово')}>
          <Icon name="check" size={18} />
          Выполнено
        </button>
      )}
      {t.can.edit && (
        <div className="grid-half">
          <button type="button" className="btn btn-ghost btn-sm" disabled={busy || !prev[t.status]} onClick={() => void run(() => api.patchTask(id, { status: prev[t.status]! }), `← ${TASK_STATUS_LABEL[prev[t.status]!]}`)}>
            ← {prev[t.status] ? TASK_STATUS_LABEL[prev[t.status]!] : 'назад'}
          </button>
          <button type="button" className="btn btn-ghost btn-sm" disabled={busy || !next[t.status]} onClick={() => void run(() => api.patchTask(id, { status: next[t.status]! }), `→ ${TASK_STATUS_LABEL[next[t.status]!]}`)}>
            {next[t.status] ? TASK_STATUS_LABEL[next[t.status]!] : 'дальше'} →
          </button>
          <button type="button" className="btn btn-outline btn-sm" onClick={() => setEdit(true)}>
            <Icon name="edit" size={16} />
            Изменить
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            disabled={busy}
            onClick={() =>
              void platform.confirm('Удалить задачу?').then(async (ok) => {
                if (!ok) return;
                try {
                  await api.deleteTask(id);
                  await onChanged();
                  onClose();
                } catch (e) {
                  fail(e);
                }
              })
            }
          >
            <Icon name="trash" size={16} />
            Удалить
          </button>
        </div>
      )}

      {p && (
        <section className="stack" style={{ gap: 8 }}>
          <div className="proc-head">
            <h2 className="section-title">
              {p.code} · {p.name}
            </h2>
          </div>
          <ProductFacts p={p} qty={t.qty} />
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => (onClose(), open({ name: 'product', id: p.id }))}>
            Карточка номенклатуры
          </button>
        </section>
      )}

      {edit && (
        <TaskEditor
          task={t}
          areas={areas}
          onClose={() => setEdit(false)}
          onSaved={async () => {
            setEdit(false);
            await load();
            await onChanged();
          }}
        />
      )}
    </Sheet>
  );
}

/** Выбор людей галочками; сотрудники выбранного участка — сверху. */
function PeoplePicker({ people, areaId, areas, value, onChange }: { people: Person[]; areaId: string | null; areas: AreaDto[]; value: string[]; onChange: (v: string[]) => void }) {
  const inArea = (p: Person) => {
    if (!areaId) return true;
    // Участок сотрудника — сам участок задачи или его родитель.
    const chain = new Set<string>();
    for (let cur: AreaDto | undefined = areas.find((a) => a.id === areaId), i = 0; cur && i < 20; i++) {
      chain.add(cur.id);
      cur = areas.find((a) => a.id === cur!.parentId);
    }
    return p.areaIds.some((a) => chain.has(a));
  };
  const sorted = [...people].sort((a, b) => Number(inArea(b)) - Number(inArea(a)) || a.name.localeCompare(b.name, 'ru'));
  return (
    <div className="stack" style={{ gap: 2, maxHeight: 260, overflowY: 'auto' }}>
      {sorted.map((p) => (
        <label key={p.id} className="check">
          <input type="checkbox" checked={value.includes(p.id)} onChange={() => onChange(value.includes(p.id) ? value.filter((x) => x !== p.id) : [...value, p.id])} />
          <span>
            {p.name}
            <span className="hint" style={{ display: 'block' }}>
              {p.position ?? 'должность не указана'}
              {!inArea(p) ? ' · другой участок' : ''}
            </span>
          </span>
        </label>
      ))}
    </div>
  );
}

const today = () => localDate(Date.now(), FACTORY_TZ);

/** Новая задача или правка: что, вид, участок, машина, номенклатура, люди, смена, срок. */
export function TaskEditor({ task, areas, onClose, onSaved }: { task: TaskDto | null; areas: AreaDto[]; onClose: () => void; onSaved: () => Promise<void> }) {
  const { fail, toast } = useApp();
  const [title, setTitle] = useState(task?.title ?? '');
  const [kind, setKind] = useState<TaskKind>(task?.kind ?? 'other');
  const [notes, setNotes] = useState(task?.notes ?? '');
  const [areaId, setAreaId] = useState(task?.areaId ?? '');
  const [equipmentId, setEquipmentId] = useState(task?.equipmentId ?? '');
  const [productId, setProductId] = useState(task?.productId ?? '');
  const [qty, setQty] = useState(task?.qty != null ? String(task.qty) : '');
  const [assignees, setAssignees] = useState<string[]>(task?.assignees.map((a) => a.id) ?? []);
  const [shiftDate, setShiftDate] = useState(task?.shiftDate ?? '');
  const [shiftNo, setShiftNo] = useState(task?.shiftNo ? String(task.shiftNo) : '');
  const [due, setDue] = useState(task?.dueAt ? task.dueAt.slice(0, 16) : '');
  const [status, setStatus] = useState<TaskStatus>(task?.status ?? 'new');
  const [people, setPeople] = useState<Person[]>([]);
  const [equipment, setEquipment] = useState<EquipmentDto[]>([]);
  const [products, setProducts] = useState<ProductListDto[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([api.people(), api.equipment(), api.products()])
      .then(([p, e, pr]) => (setPeople(p), setEquipment(e), setProducts(pr)))
      .catch(fail);
  }, [fail]);

  async function save() {
    const body = {
      title,
      kind,
      notes: notes.trim() || null,
      areaId: areaId || null,
      equipmentId: equipmentId || null,
      productId: productId || null,
      qty: qty.trim() ? Number(qty.replace(',', '.')) : null,
      assigneeIds: assignees,
      shiftDate: shiftDate || null,
      shiftNo: shiftNo ? Number(shiftNo) : null,
      dueAt: due ? new Date(due).toISOString() : null,
    };
    setBusy(true);
    try {
      if (task) await api.patchTask(task.id, { ...body, status });
      else await api.createTask(body);
      platform.haptic('success');
      toast(task ? 'Задача сохранена' : 'Задача на доске');
      await onSaved();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }

  const sortedAreas = [...areas].sort((a, b) => areaPath(areas, a.id).localeCompare(areaPath(areas, b.id), 'ru'));
  return (
    <Sheet title={task ? 'Изменить задачу' : 'Новая задача'} onClose={onClose}>
      <div className="field">
        <label htmlFor="tt">Что сделать</label>
        <textarea id="tt" className="input" rows={2} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Например: заменить ремень на конвейере" />
      </div>
      <div className="field">
        <span className="label">Вид</span>
        <div className="chips">
          {(['other', 'production', 'changeover'] as TaskKind[]).map((k) => (
            <button key={k} type="button" className="chip" aria-pressed={kind === k} onClick={() => setKind(k)}>
              {TASK_KIND_LABEL[k]}
            </button>
          ))}
        </div>
      </div>
      <div className="field">
        <label htmlFor="ta">Участок</label>
        <select id="ta" className="input" value={areaId} onChange={(e) => setAreaId(e.target.value)}>
          <option value="">— не указан</option>
          {sortedAreas.map((a) => (
            <option key={a.id} value={a.id}>
              {areaPath(areas, a.id)}
            </option>
          ))}
        </select>
      </div>
      <div className="field">
        <label htmlFor="te">Машина</label>
        <select id="te" className="input" value={equipmentId} onChange={(e) => setEquipmentId(e.target.value)}>
          <option value="">— не указана</option>
          {equipment.map((e) => (
            <option key={e.id} value={e.id}>
              {e.name} · {e.code}
            </option>
          ))}
        </select>
      </div>
      <div className="grid-half">
        <div className="field">
          <label htmlFor="tp">Номенклатура</label>
          <select id="tp" className="input" value={productId} onChange={(e) => setProductId(e.target.value)}>
            <option value="">—</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.code}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="tq">Количество</label>
          <input id="tq" className="input" inputMode="decimal" value={qty} onChange={(e) => setQty(e.target.value)} />
        </div>
      </div>
      <div className="grid-half">
        <div className="field">
          <label htmlFor="tsd">Смена: дата</label>
          <input id="tsd" className="input" type="date" value={shiftDate} onChange={(e) => setShiftDate(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="tsn">Смена</label>
          <select id="tsn" className="input" value={shiftNo} onChange={(e) => setShiftNo(e.target.value)}>
            <option value="">—</option>
            <option value="1">1 смена</option>
            <option value="2">2 смена</option>
            <option value="3">3 смена</option>
          </select>
        </div>
      </div>
      <div className="field">
        <label htmlFor="tdue">Срок</label>
        <input id="tdue" className="input" type="datetime-local" value={due} onChange={(e) => setDue(e.target.value)} />
      </div>
      <div className="field">
        <span className="label">Исполнители</span>
        <PeoplePicker people={people} areaId={areaId || null} areas={areas} value={assignees} onChange={setAssignees} />
      </div>
      <div className="field">
        <label htmlFor="tn">Подробности</label>
        <textarea id="tn" className="input" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </div>
      {task && (
        <div className="field">
          <span className="label">Колонка</span>
          <div className="chips">
            {TASK_STATUSES.map((s) => (
              <button key={s} type="button" className="chip" aria-pressed={status === s} onClick={() => setStatus(s)}>
                {TASK_STATUS_LABEL[s]}
              </button>
            ))}
          </div>
        </div>
      )}
      <button type="button" className="btn btn-primary btn-block" disabled={busy} onClick={() => void save()}>
        {task ? 'Сохранить' : 'Поставить задачу'}
      </button>
    </Sheet>
  );
}

type Row = { key: string; productId: string; qty: string; equipmentId: string; changeover: boolean };

/**
 * Задание на смену: дата, смена, участок, кто работает, что делать (номенклатуры с количеством, машина, переналадка).
 * На доске появятся задачи производства, переналадки и проверки оборудования участка.
 */
export function ShiftSheet({ areas, onClose, onDone }: { areas: AreaDto[]; onClose: () => void; onDone: () => Promise<void> }) {
  const { fail, toast } = useApp();
  const [date, setDate] = useState(today());
  const [shiftNo, setShiftNo] = useState(1);
  const [areaId, setAreaId] = useState('');
  const [assignees, setAssignees] = useState<string[]>([]);
  const [rows, setRows] = useState<Row[]>([]);
  const [checks, setChecks] = useState(true);
  const [people, setPeople] = useState<Person[]>([]);
  const [equipment, setEquipment] = useState<EquipmentDto[]>([]);
  const [products, setProducts] = useState<ProductListDto[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([api.people(), api.equipment(), api.products()])
      .then(([p, e, pr]) => (setPeople(p), setEquipment(e), setProducts(pr)))
      .catch(fail);
  }, [fail]);

  // Машины выбранного участка (вместе с вложенными).
  const areaEq = useMemo(() => {
    if (!areaId) return [];
    const inside = new Set([areaId]);
    for (let grew = true; grew; ) {
      grew = false;
      for (const a of areas) if (a.parentId && inside.has(a.parentId) && !inside.has(a.id)) (inside.add(a.id), (grew = true));
    }
    return equipment.filter((e) => e.areaId && inside.has(e.areaId));
  }, [areaId, areas, equipment]);
  const checksCount = areaEq.reduce((n, e) => n + e.checkProcedures.length, 0);
  const setRow = (key: string, patch: Partial<Row>) => setRows((l) => l.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  async function save() {
    if (!areaId) return fail(new Error('Выберите участок'));
    setBusy(true);
    try {
      const r = await api.createShift({
        date,
        shiftNo,
        areaId,
        assigneeIds: assignees,
        checks,
        rows: rows
          .filter((x) => x.productId)
          .map((x) => ({ productId: x.productId, qty: x.qty.trim() ? Number(x.qty.replace(',', '.')) : null, equipmentId: x.equipmentId || null, changeover: x.changeover })),
      });
      platform.haptic('success');
      toast(`На доске: ${r.created} ${plural(r.created, 'задача', 'задачи', 'задач')}`);
      await onDone();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }

  const sortedAreas = [...areas].sort((a, b) => areaPath(areas, a.id).localeCompare(areaPath(areas, b.id), 'ru'));
  return (
    <Sheet title="Задание на смену" onClose={onClose}>
      <div className="grid-half">
        <div className="field">
          <label htmlFor="shd">Дата</label>
          <input id="shd" className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
        <div className="field">
          <span className="label">Смена</span>
          <div className="seg" role="group" aria-label="Смена">
            {[1, 2, 3].map((n) => (
              <button key={n} type="button" aria-pressed={shiftNo === n} onClick={() => setShiftNo(n)}>
                {n}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="field">
        <label htmlFor="sha">Участок</label>
        <select id="sha" className="input" value={areaId} onChange={(e) => setAreaId(e.target.value)}>
          <option value="">— выберите</option>
          {sortedAreas.map((a) => (
            <option key={a.id} value={a.id}>
              {areaPath(areas, a.id)}
            </option>
          ))}
        </select>
      </div>
      <div className="field">
        <span className="label">Кто работает</span>
        <PeoplePicker people={people} areaId={areaId || null} areas={areas} value={assignees} onChange={setAssignees} />
      </div>

      <div className="field">
        <span className="label">Что делать</span>
        {rows.map((r, i) => (
          <div key={r.key} className="proc-edit" style={{ gap: 6 }}>
            <div className="row" style={{ gap: 6 }}>
              <select className="input grow" aria-label={`Номенклатура ${i + 1}`} value={r.productId} onChange={(e) => setRow(r.key, { productId: e.target.value })}>
                <option value="">— номенклатура</option>
                {products.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.code} · {p.name}
                  </option>
                ))}
              </select>
              <button type="button" className="icon-btn" aria-label="Убрать строку" onClick={() => setRows((l) => l.filter((x) => x.key !== r.key))}>
                <Icon name="trash" size={16} />
              </button>
            </div>
            <div className="grid-half">
              <input className="input" inputMode="decimal" placeholder="Количество, шт." aria-label={`Количество ${i + 1}`} value={r.qty} onChange={(e) => setRow(r.key, { qty: e.target.value })} />
              <select className="input" aria-label={`Машина ${i + 1}`} value={r.equipmentId} onChange={(e) => setRow(r.key, { equipmentId: e.target.value })}>
                <option value="">— машина</option>
                {areaEq.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.name}
                  </option>
                ))}
              </select>
            </div>
            <label className="check">
              <input type="checkbox" checked={r.changeover} onChange={(e) => setRow(r.key, { changeover: e.target.checked })} />
              <span>Нужна переналадка машины на эту номенклатуру</span>
            </label>
          </div>
        ))}
        {products.length === 0 ? (
          <span className="hint">Номенклатура не загружена — «Ещё → Номенклатура → Из Excel».</span>
        ) : (
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setRows((l) => [...l, { key: Math.random().toString(36).slice(2), productId: '', qty: '', equipmentId: '', changeover: false }])}>
            <Icon name="plus" size={16} />
            Номенклатура
          </button>
        )}
      </div>

      <label className="check">
        <input type="checkbox" checked={checks} onChange={(e) => setChecks(e.target.checked)} />
        <span>
          Проверки оборудования участка
          <span className="hint" style={{ display: 'block' }}>
            {areaId ? `${checksCount} ${checksCount === 1 ? 'проверка' : 'проверок'}: до смены, в смену, после смены — по спискам машин` : 'Выберите участок'}
          </span>
        </span>
      </label>
      <button type="button" className="btn btn-primary btn-block" disabled={busy} onClick={() => void save()}>
        Поставить на доску
      </button>
    </Sheet>
  );
}
